import { FilterableSinglePicker } from "@flaminem/ui-components";
import { Controller, FieldPath, FieldValues, useFormContext } from "react-hook-form";

import { PickerFormFieldPropsType } from "src/components/Pickers/typings.ts";
import { useImportSpecificationsQuery } from "src/graphql/import-export/queries/ImportSpecificationsQuery.ts";
import { useT } from "src/internationalization";
import { mapToApiImportSpecificationType } from "src/mappers/importSpecification.mappers";
import { CaseType } from "src/types/case";
import { ImportSpecificationType } from "src/types/importSpecification";

function ImportSpecificationSinglePickerFormField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  id,
  name,
  rules,
  invalidText,
  titleText,
  helperText,
  type,
  caseType,
}: PickerFormFieldPropsType<string, TFieldValues, TName> & {
  type: ImportSpecificationType;
  caseType?: CaseType;
}) {
  const t = useT();
  const { setError } = useFormContext<TFieldValues, TName>();
  const { data, loading, error } = useImportSpecificationsQuery({
    type: mapToApiImportSpecificationType(type),
    caseTypeCode: caseType ? caseType.code : undefined,
    onError: (error) => setError(name, error),
  });

  const getInvalidText = () => {
    if (typeof invalidText === "string") return invalidText;
    return t("component.picker.import.specification.invalid");
  };

  return (
    <Controller
      name={name}
      rules={rules}
      render={({ field: { onChange, value }, fieldState: { error: fieldError } }) => (
        <FilterableSinglePicker
          options={data ?? []}
          value={value}
          id={id}
          onChange={onChange}
          getOptionValue={(item) => item.id}
          getOptionLabel={(item) => item.name}
          invalid={!!fieldError}
          invalidText={getInvalidText()}
          label={typeof titleText === "string" ? titleText : undefined}
          helperText={typeof helperText === "string" ? helperText : undefined}
          loading={loading}
          loadingOptionsError={!!error}
          languageTexts={{
            noOptions: t("general.info.no.options"),
            errorLoadingOptions: t("general.info.error.loading.options"),
            emptySearch: t("general.info.start.typing.to.search"),
          }}
        />
      )}
    />
  );
}

export default ImportSpecificationSinglePickerFormField;
